import React, { useState, useMemo } from "react";
import { Card, CardContent, Typography, Box, Select, MenuItem } from "@mui/material";
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";
import styles from "./MetricsOverTime.module.scss";

dayjs.extend(minMax);

const MetricsOverTime = ({ data = [], granularity = "auto", periodStart, periodEnd }) => {
    const [metric, setMetric] = useState("Average Rating");

    const chartData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        const mapped = data.map((item) => ({
            date: item.reportDate,
            dateNum: dayjs(item.reportDate).valueOf(),
            value:
                metric === "Average Rating"
                    ? item.averageRating
                    : metric === "Satisfaction Rate"
                    ? item.satisfactionRate * 100
                    : metric === "Total Responses"
                    ? item.totalResponses
                    : item.issuesReported,
        }));
        return mapped.filter((d) => d.value != null && d.value !== 0);
    }, [data, metric]);

    const { ticks, tickFormatter, minTs, maxTs } = useMemo(() => {
        const dates = chartData.map((d) => dayjs(d.date));
        const dataMin = dates.length ? dayjs.min(...dates) : undefined;
        const dataMax = dates.length ? dayjs.max(...dates) : undefined;
        const min = periodStart ? dayjs(periodStart) : dataMin;
        const max = periodEnd ? dayjs(periodEnd) : dataMax;
        const useWeekly = granularity === "weekly";
        const useMonthly = granularity === "monthly";
        const spanDays = max.diff(min, "day");
        const startMonth = min.startOf("month");
        const endMonthStart = max.startOf("month");
        const ticks = [];

        if (!chartData.length || !min || !max) {
            return { ticks: undefined, tickFormatter: (d) => d, minTs: undefined, maxTs: undefined };
        }

        if (useWeekly || (!useMonthly && spanDays <= 45)) {
            const monthStart = min.startOf("month");
            const monthEnd = monthStart.endOf("month");
            const out = [monthStart.valueOf()];
            let cur = monthStart.add(6, "day");
            while (cur.isBefore(monthEnd.add(1, "day"))) {
                out.push(cur.valueOf());
                cur = cur.add(7, "day");
            }
            return {
                ticks: out,
                tickFormatter: (ts) => dayjs(ts).format("MMM D"),
                minTs: monthStart.valueOf(),
                maxTs: monthEnd.valueOf(),
            };
        }

        let cur = startMonth.clone();
        while (cur.isBefore(endMonthStart) || cur.isSame(endMonthStart)) {
            ticks.push(cur.valueOf());
            cur = cur.add(1, "month");
        }
        const lastTs = endMonthStart.valueOf();
        return {
            ticks,
            tickFormatter: (ts) => (ts === lastTs ? "" : dayjs(ts).format("MMM")),
            minTs: startMonth.valueOf(),
            maxTs: endMonthStart.valueOf(),
        };
    }, [chartData, granularity, periodStart, periodEnd]);

    const yAxisLabel =
        metric === "Average Rating"
            ? "Average Rating"
            : metric === "Satisfaction Rate"
            ? "Satisfaction Rate"
            : metric === "Total Responses"
            ? "Total Responses"
            : "Issues Reported";

    return (
        <Card className={styles.card}>
            <CardContent className={styles.cardContent}>
                <div className={styles.header}>
                    <Typography variant="subtitle3" className={styles.title}>
                        Metrics Over Time
                    </Typography>

                    <Select
                        size="small"
                        value={metric}
                        onChange={(e) => setMetric(e.target.value)}
                        variant="outlined"
                        className={styles.metricSelect}
                    >
                        <MenuItem value="Average Rating">Average Rating</MenuItem>
                        <MenuItem value="Satisfaction Rate">Satisfaction Rate</MenuItem>
                        <MenuItem value="Total Responses">Total Responses</MenuItem>
                        <MenuItem value="Issues Reported">Issues Reported</MenuItem>
                    </Select>
                </div>

                <div className={styles.chartWrapper}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                            <CartesianGrid stroke="#E5E7EB" strokeDasharray="4 6" vertical={false} />
                            <XAxis
                                dataKey="dateNum"
                                type="number"
                                scale="time"
                                domain={[minTs ?? "auto", maxTs ?? "auto"]}
                                axisLine={{ stroke: "#E5E7EB" }}
                                tickLine={false}
                                tick={{ fill: "#6B7280", fontSize: 12 }}
                                tickMargin={8}
                                ticks={ticks}
                                tickFormatter={tickFormatter}
                                padding={{ left: 0, right: 0 }}
                                allowDataOverflow
                            />
                            <YAxis
                                domain={
                                    metric === "Average Rating"
                                        ? [0, 5]
                                        : metric === "Satisfaction Rate"
                                        ? [0, 100]
                                        : ["auto", "auto"]
                                }
                                ticks={
                                    metric === "Average Rating"
                                        ? [0, 1, 2, 3, 4, 5]
                                        : metric === "Satisfaction Rate"
                                        ? [0, 20, 40, 60, 80, 100]
                                        : undefined
                                }
                                axisLine={{ stroke: "#E5E7EB" }}
                                tickLine={false}
                                tick={{ fill: "#6B7280", fontSize: 12 }}
                                tickMargin={8}
                                label={{
                                    value: yAxisLabel,
                                    angle: -90,
                                    position: "insideLeft",
                                    offset: 8,
                                    fill: "#6B7280",
                                    fontSize: 12,
                                }}
                            />
                            <Tooltip
                                formatter={(value) => {
                                    const v = Number(value);
                                    if (metric === "Average Rating") return [`${v.toFixed(1)}`, metric];
                                    if (metric === "Satisfaction Rate") return [`${v.toFixed(0)}%`, metric];
                                    return [String(v), metric];
                                }}
                                labelFormatter={(label) => dayjs(label).format("MMM D, YYYY")}
                            />
                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke="#1A73E8"
                                strokeWidth={2}
                                fill="#1A73E8"
                                fillOpacity={0.18}
                                dot={{ r: 3.5, stroke: "#1A73E8", fill: "#1A73E8" }}
                                activeDot={{ r: 5 }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
};

export default MetricsOverTime;
